import React, { useEffect, useState } from "react";
import {
    Alert, AlertIcon, Badge, Box, Flex, Heading, Select, SimpleGrid, Skeleton, Text, useToast,
} from '@chakra-ui/react';
import { getConfigEmissorNfeMl, salvarConfigEmissorNfeMl } from "../services/api";
import { formatarCnpj } from "../components/EmissorNfeMl";

// Emissor padrao da NF-e das vendas do Mercado Livre por conta. O despacho e a
// emissao em massa usam este padrao e deixam trocar so naquele lote.
const EmissorNfeMl = () => {
    const toast = useToast();
    const [config, setConfig] = useState(null);
    const [carregando, setCarregando] = useState(true);
    const [salvando, setSalvando] = useState(null);

    useEffect(() => {
        let vivo = true;
        getConfigEmissorNfeMl().then((dados) => {
            if (!vivo) return;
            setConfig(dados);
            setCarregando(false);
        });
        return () => { vivo = false; };
    }, []);

    const trocar = async (conta, emissor) => {
        setSalvando(conta);
        const resposta = await salvarConfigEmissorNfeMl(conta, emissor);
        setSalvando(null);
        if (!resposta.ok) {
            toast({ title: 'Não foi possível salvar', description: resposta.mensagem, status: 'error', duration: 6000, isClosable: true });
            return;
        }
        setConfig(resposta.data);
        toast({
            title: `Conta ${conta}: emissor ${emissor === 'interno' ? 'interno (TAGA)' : 'faturador do Mercado Livre'}`,
            status: 'success',
            duration: 3000,
        });
    };

    return (
        <Box p={{ base: 4, md: 6 }} bg="white" borderRadius="lg" borderWidth="1px" w="100%" boxShadow="sm">
            <Heading size="md" mb={1}>Emissor da NF-e das vendas do Mercado Livre</Heading>
            <Text fontSize="sm" color="gray.600" mb={4}>
                Padrão de cada conta. No despacho e na emissão em massa dá para trocar só naquele lote.
                No interno a nota sai com o CNPJ da conta, as regras fiscais novas e o XML é anexado no envio do ML.
            </Text>

            {carregando && (
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                    {[1, 2, 3].map((i) => <Skeleton key={i} h="110px" rounded="md" />)}
                </SimpleGrid>
            )}

            {!carregando && !config?.contas?.length && (
                <Alert status="error" rounded="md">
                    <AlertIcon />
                    Não consegui carregar a configuração do emissor. Recarregue a página.
                </Alert>
            )}

            {!carregando && config?.contas?.length > 0 && (
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                    {config.contas.map((linha) => (
                        <Box key={linha.conta} borderWidth="1px" borderColor="gray.200" rounded="md" p={3}>
                            <Flex justify="space-between" align="center" mb={1} gap={2}>
                                <Text fontWeight="bold" fontSize="sm">Conta {linha.conta}</Text>
                                <Badge colorScheme={linha.emissor_padrao === 'interno' ? 'cyan' : 'yellow'} fontSize="10px">
                                    {linha.emissor_padrao === 'interno' ? 'Interno' : 'Faturador ML'}
                                </Badge>
                            </Flex>
                            <Text fontSize="xs" color="gray.600" noOfLines={1}>{linha.apelido || '—'}</Text>
                            <Text fontSize="xs" color={linha.cnpj ? 'gray.600' : 'red.600'} mb={2}>
                                {linha.cnpj ? `CNPJ ${formatarCnpj(linha.cnpj)}` : (linha.erro || 'CNPJ indisponível')}
                            </Text>
                            <Select
                                size="sm"
                                aria-label={`Emissor padrão da conta ${linha.conta}`}
                                value={linha.emissor_padrao}
                                isDisabled={salvando !== null}
                                onChange={(e) => trocar(linha.conta, e.target.value)}
                            >
                                {(config.emissores || []).map((opcao) => (
                                    <option key={opcao.valor} value={opcao.valor}>{opcao.rotulo}</option>
                                ))}
                            </Select>
                        </Box>
                    ))}
                </SimpleGrid>
            )}
        </Box>
    );
};

export default EmissorNfeMl;
